import React, { useState } from 'react';
import { Ejecutiva } from '../../types/Altas';
import { Badge, Button, Modal } from '../ui';
import { PlusIcon } from '../icons';
import { inputClass, labelClass } from './altasUi';

interface Props {
  ejecutivas: Ejecutiva[];
  onCrear: (nombre: string) => Promise<boolean>;
  onActivar: (id: number, activa: boolean) => Promise<void>;
  onClose: () => void;
}

/**
 * Alta y baja de ejecutivas. Una ejecutiva que se va se desactiva, no se
 * borra: sus ventas tienen que seguir apareciendo en el reporte histórico.
 */
const EjecutivasModal: React.FC<Props> = ({ ejecutivas, onCrear, onActivar, onClose }) => {
  const [nombre, setNombre] = useState('');
  const [guardando, setGuardando] = useState(false);

  const limpio = nombre.trim();
  const repetida = ejecutivas.some((e) => e.nombre.toLowerCase() === limpio.toLowerCase());
  const valido = limpio !== '' && !repetida && !guardando;

  const crear = async () => {
    if (!valido) return;
    setGuardando(true);
    const ok = await onCrear(limpio);
    setGuardando(false);
    if (ok) setNombre('');
  };

  return (
    <Modal titulo="Ejecutivos comerciales" onClose={onClose} size="lg">
      <div className="space-y-5">
        <div>
          <label htmlFor="ejecutiva-nombre" className={labelClass}>
            Nuevo Ejecutivo
          </label>
          <div className="flex gap-2">
            <input
              id="ejecutiva-nombre"
              type="text"
              value={nombre}
              autoFocus
              maxLength={120}
              placeholder="Nombre y apellido"
              onChange={(e) => setNombre(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && crear()}
              className={inputClass}
            />
            <Button type="button" variant="primary" icon={<PlusIcon />} disabled={!valido} onClick={crear}>
              Agregar
            </Button>
          </div>
          {repetida && (
            <p className="mt-1.5 text-[11px] text-amber-600">Ya hay un ejecutivo con ese nombre.</p>
          )}
        </div>

        {ejecutivas.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {ejecutivas.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-3 py-2">
                <span className="flex min-w-0 items-center gap-2">
                  <span className={`truncate text-sm ${e.activa ? 'text-gappex-secondary' : 'text-slate-400'}`}>
                    {e.nombre}
                  </span>
                  {!e.activa && <Badge tone="neutral">Inactiva</Badge>}
                </span>
                <Button
                  type="button"
                  variant={e.activa ? 'ghost' : 'secondary'}
                  onClick={() => onActivar(e.id, !e.activa)}
                  title={
                    e.activa
                      ? 'Deja de aparecer para cargar altas nuevas. Sus ventas siguen contando.'
                      : 'Vuelve a aparecer para cargar altas.'
                  }
                >
                  {e.activa ? 'Desactivar' : 'Reactivar'}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
};

export default EjecutivasModal;
